<template>
  <div id="rota-user-month-view" class="rota-user-month-view">
    <div v-if="events.length === 0" class="alert alert-block alert-warning">
      We could not find any shifts for this month
    </div>
    <FullCalendar ref="fullCalendarRef" :options="calendarOptions">
      <template #eventContent="arg">
        <div class="event-wrapper">
          <rota-agenda-shift-view
            :rota-type="arg.event.extendedProps.rotaType"
            :rota-status="arg.event.extendedProps.rotaStatus"
            :rota-id="arg.event.extendedProps.rotaId"
            :shift="arg.event.extendedProps.shift"
            :user-id="userId"
            :activities="activities"
            :always-expanded="true"
            :hide-assigned="true"
          />
        </div>
      </template>
    </FullCalendar>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import { min, max, startOfMonth, endOfMonth } from "date-fns";
import FullCalendar from "@fullcalendar/vue3";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useAuthStore } from "@switchsystems/coactive-vue-lib";
import RotaAgendaShiftView from "@/components/rota/agenda/RotaAgendaShiftView.vue";
import {
  userIsOnThisRota,
  agencyIsOnThisRota,
} from "@/lib/rota/rotauser-functions";
import { formatTimeHoursMins, todayIsInRota } from "@/lib/date-functions";
import {
  filterShiftsForUser,
  filterShiftsWithCoverRequestsOrNotCurrentlyAssigned,
  filterCurrentShiftsForAgencyId,
  getShiftsForUserId,
} from "@/lib/rota/shift-functions";

export default {
  name: "RotaUserMonthView",
  components: {
    FullCalendar,
    RotaAgendaShiftView,
  },
  props: {
    rotas: {
      type: Array,
      required: true,
    },
    activities: {
      type: [Boolean, Array],
      required: true,
    },
  },
  setup(props) {
    const authStore = useAuthStore();
    const fullCalendarRef = ref(null);

    const userId = computed(() => {
      return authStore.isAgencyUser
        ? authStore.user.agencyId
        : authStore.user.id;
    });

    const startDate = computed(() => {
      (props.rotas || []).forEach((rota) => {
        if (
          todayIsInRota(rota.startDate) &&
          (userIsOnThisRota(authStore.user.id, rota) ||
            (authStore.isAgencyUser &&
              agencyIsOnThisRota(authStore.user.agencyId, rota)))
        ) {
          return rota.startDate;
        }
      });
      return null;
    });

    const rotaShiftsForUserOrNobody = computed(() => {
      // filter the shifts array down to the current user or nobody,
      // to reduce the number of shifts we're passing around and iterating over
      const userId = authStore.isAgencyUser
        ? authStore.user.agencyId
        : authStore.user.id;

      const uniqueShifts = new Map();

      (props.rotas || []).forEach((rota) => {
        filterShiftsWithCoverRequestsOrNotCurrentlyAssigned(
          rota.shifts,
        ).forEach((shift) => {
          shift.rotaId = rota.id;
          uniqueShifts.set(shift.id, shift);
        });

        if (authStore.isAgencyUser) {
          filterCurrentShiftsForAgencyId(
            rota.shifts,
            authStore.user.agencyId,
          ).forEach((shift) => {
            shift.rotaId = rota.id;
            uniqueShifts.set(shift.id, shift);
          });
        }

        filterShiftsForUser(rota.shifts, userId).forEach((shift) => {
          shift.rotaId = rota.id;
          uniqueShifts.set(shift.id, shift);
        });
      });

      // convert the map back to an array
      return Array.from(uniqueShifts.values());
    });

    const events = computed(() => {
      const events = [];

      getShiftsForUserId(
        rotaShiftsForUserOrNobody.value,
        authStore.isAgencyUser ? authStore.user.agencyId : authStore.user.id,
      ).forEach((shift) => {
        const rota = props.rotas.find((r) => r.id === shift.rotaId);

        events.push({
          id: shift.id,
          title: shift.title,
          start: shift.startDate,
          end: shift.endDate,
          allDay: false,
          startText: formatTimeHoursMins(shift.startDate),
          endText: formatTimeHoursMins(shift.endDate),
          shift,
          rotaType: rota.type,
          rotaStatus: rota.status,
          rotaId: rota.id,
        });
      });

      return events;
    });

    const calendarOptions = computed(() => {
      return {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: "dayGridMonth",
        initialDate: startDate.value,
        events: events.value,
        firstDay: 1,
        showNonCurrentDates: true,
        nowIndicator: true,
        eventMinWidth: 50,
        height: "auto",
        rerenderDelay: 50,
        headerToolbar: {
          right: "",
          left: "title",
          center: "prev,next",
        },
        footerToolbar: {
          right: "",
          left: "",
          center: "prev,next",
        },
        validRange: {
          start: startOfMonth(
            min((props.rotas || []).map((rota) => rota.startDate)),
          ),
          end: endOfMonth(max((props.rotas || []).map((rota) => rota.endDate))),
        },
      };
    });

    return {
      fullCalendarRef,
      calendarOptions,
      startDate,
      userId,
      events,
    };
  },
};
</script>
